import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import * as yup from 'yup';

import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
    CheckboxGroup, FormControlMessage, Loader, Title,
} from 'ui-library';
import useUploadBundledDocuments from 'pages/DocLibraryPage/hooks/useUploadBundledDocuments';
import { DocStatus } from 'components/DocBox/DocBox.types';
import { useYupValidationResolver } from 'hooks/useYupValidationResolver';
import { getValidationMessage } from 'utils/validation';

import Divider from 'ui-library/components/Divider';
import DocBoxGeneric from 'components/DocBox/DocBoxGeneric';
import SelectElement from 'components/AtomicStructure/atoms/SelectElement';
import ButtonsBlockRow from 'components/ButtonsBlockRow';

import {
    LangSource,
    languagesAdapt,
} from 'translations/adaptors/languagesAdapt';

import ReplaceDocumentsConfirmModal from './ReplaceDocumentsConfirmModal';

import './UploadBundledDocuments.css';

interface FormValues {
    process: string | null;
    product: string | null;
    documentCategory: string | null;
    processes: string[];
    products: string[];
    documents: Record<string, File | null>;
}

const UploadBundledDocuments = ({
    onBack,
    onSuccess,
    backDisabled,
    saveButtonDisabled,
    productOptions = [],
}: {
    onBack: () => void;
    onSuccess?: () => void;
    backDisabled?: boolean;
    saveButtonDisabled?: boolean;
    productOptions: { value: string; label: string }[];
}) => {
    const { t } = useTranslation();
    const { mandatoryError } = getValidationMessage();

    const languages: LangSource[] = useMemo(
        () => languagesAdapt(globalThis.availableLanguages, t),
        [t],
    );

    const schema = useMemo(
        () => yup.object({
            process: yup.string().nullable(),
            product: yup.string().nullable(),
            documentCategory: yup
                .string()
                .nullable()
                .required(mandatoryError),
            processes: yup.array().of(yup.string()).min(1, mandatoryError),
            products: yup.array().of(yup.string()).min(1, mandatoryError),
            documents: yup.object(
                Object.fromEntries(
                    languages.map((lang) => [
                        lang.value,
                        yup.mixed<File>().nullable(),
                    ]),
                ),
            ),
        }),
        [mandatoryError, languages],
    );

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm<FormValues>({
        resolver: useYupValidationResolver(schema),
        defaultValues: {
            process: null,
            product: null,
            documentCategory: null,
            processes: [],
            products: [],
            documents: Object.fromEntries(
                languages.map((lang) => [lang.value, null]),
            ),
        },
    });

    const processFilter = watch('process');
    const productFilter = watch('product');
    const documentCategory = watch('documentCategory');
    const documents = watch('documents');

    const {
        documentCategoryOptions,
        processFilterOptions,
        processCheckboxOptions,
        productCheckboxOptions,
        selectedSecurity,
        selectedModelPortfolio,
        isUploading,
        isCategoriesLoading,
        isModelPortfoliosLoading,
        isSecuritiesLoading,
        uploadDocuments,
    } = useUploadBundledDocuments({
        productOptions,
        processFilter,
        productFilter,
        documentCategory,
        onUploadSuccess: onSuccess ?? onBack,
    });

    const selectedDocumentCategory = useMemo(
        () => documentCategoryOptions.find(
            (option) => option.value === documentCategory,
        ),
        [documentCategoryOptions, documentCategory],
    );

    // Drop the selected category as soon as the filters no longer offer it.
    useEffect(() => {
        if (documentCategory && !selectedDocumentCategory) {
            setValue('documentCategory', null);
        }
    }, [documentCategory, selectedDocumentCategory, setValue]);

    const availableProcesses = useMemo(
        () => processCheckboxOptions.map((option) => String(option.value)),
        [processCheckboxOptions],
    );
    const availableProducts = useMemo(
        () => productCheckboxOptions.map((option) => String(option.value)),
        [productCheckboxOptions],
    );

    // Every process/product the selected document belongs to is ticked by default.
    useEffect(() => {
        setValue('processes', availableProcesses);
        setValue('products', availableProducts);
    }, [availableProcesses.join(), availableProducts.join(), setValue]);

    // The sections are only shown when the document is used in more than one process/product.
    const showProcessSection = processCheckboxOptions.length > 1;
    const showProductSection = productCheckboxOptions.length > 1;
    const showSelectionSections = showProcessSection || showProductSection;
    const showBibDetails = !!selectedSecurity || !!selectedModelPortfolio;

    const hasAtLeastOneDocument = Object.values(documents ?? {}).some(Boolean);

    const [documentsToReplace, setDocumentsToReplace] = useState<FormValues | null>(null);

    // Resolved from the submitted snapshot, not from the live form value.
    const replacedDocumentName = useMemo(() => {
        const category = documentsToReplace?.documentCategory;

        if (!category) return undefined;

        return documentCategoryOptions.find(
            (option) => option.value === category,
        )?.label ?? category;
    }, [documentsToReplace, documentCategoryOptions]);

    // Only the languages a new document has been uploaded for.
    const uploadedLanguages = useMemo(
        () => languages
            .filter(({ value }) => Boolean(documentsToReplace?.documents?.[value]))
            .map(({ value }) => String(value).toLocaleUpperCase()),
        [languages, documentsToReplace],
    );

    const selectedProcessLabels = useMemo(
        () => (documentsToReplace?.processes ?? []).map(
            (value) => processCheckboxOptions.find(
                (option) => String(option.value) === String(value),
            )?.label ?? value,
        ),
        [documentsToReplace, processCheckboxOptions],
    );

    const selectedProductLabels = useMemo(
        () => (documentsToReplace?.products ?? []).map(
            (value) => productCheckboxOptions.find(
                (option) => String(option.value) === String(value),
            )?.label ?? value,
        ),
        [documentsToReplace, productCheckboxOptions],
    );

    // The upload API must only be called once the user confirms in the pop-up.
    const onReplaceDocuments = useCallback(
        (data: FormValues) => setDocumentsToReplace(data),
        [],
    );

    const closeConfirmation = useCallback(() => setDocumentsToReplace(null), []);

    const confirmReplaceDocuments = useCallback(async () => {
        if (!documentsToReplace) return;

        try {
            await uploadDocuments(documentsToReplace);
        } finally {
            setDocumentsToReplace(null);
        }
    }, [documentsToReplace, uploadDocuments]);

    return (
        <form
            onSubmit={handleSubmit(onReplaceDocuments)}
            className="upload-bundled-documents"
        >
            <div className="content-top">
                <p className="mb-16 upload-warning">
                    {t(
                        'advisoryDashboard.documents.uploadDocuments.warning',
                    )}
                </p>
                <div className="mb-16 flex flex-row gap-20">
                    <Controller
                        name="process"
                        control={control}
                        render={({ field }) => (
                            <SelectElement
                                onChange={field.onChange}
                                options={processFilterOptions}
                                value={field.value}
                                placeholder={t(
                                    'advisoryDashboard.documents.uploadDocuments.selectProcess',
                                )}
                                className="upload-bundled-documents__select"
                                error={(errors.process as any)?.message}
                            />
                        )}
                    />
                    <Controller
                        name="product"
                        control={control}
                        render={({ field }) => (
                            <SelectElement
                                onChange={field.onChange}
                                options={productOptions}
                                value={field.value}
                                placeholder={t(
                                    'advisoryDashboard.documents.uploadDocuments.selectProduct',
                                )}
                                className="upload-bundled-documents__select"
                                error={(errors.product as any)?.message}
                            />
                        )}
                    />
                </div>

                <Divider className="mb-32 mt-32" />

                <Title type={3} className="mb-16">
                    {t(
                        'advisoryDashboard.documents.uploadDocuments.selectDocumentCategory',
                    )}
                </Title>

                <Controller
                    name="documentCategory"
                    control={control}
                    render={({ field }) => (
                        <SelectElement
                            onChange={field.onChange}
                            options={documentCategoryOptions}
                            value={field.value}
                            disabled={isCategoriesLoading}
                            placeholder={t(
                                'advisoryDashboard.documents.uploadDocuments.selectDocument',
                            )}
                            className="upload-bundled-documents__select_full mb-32"
                            error={(errors.documentCategory as any)?.message}
                        />
                    )}
                />

                {showBibDetails && (
                    <>
                        {(isModelPortfoliosLoading || isSecuritiesLoading) && <div className="upload-bundle-document--model-portfolio-loader mb-20"><Loader /></div>}
                        {!!selectedModelPortfolio && (
                            <Title
                                type={3}
                                className="mb-16 upload-bundled-documents__model-portfolio-title"
                            >
                                {t(
                                    'advisoryDashboard.documents.uploadDocuments.ModelPortfolioTitle',
                                    'Model Portfolio',
                                )}
                                :
                                {' '}
                                <span>{selectedModelPortfolio?.Name}</span>
                            </Title>
                        )}

                        {!!selectedSecurity && (
                            <Title
                                type={3}
                                className="mb-32 upload-bundled-documents__security-title"
                            >
                                {t(
                                    'advisoryDashboard.documents.uploadDocuments.SecurityTitle',
                                    'Security',
                                )}
                                :
                                {' '}
                                <span>{selectedSecurity?.name}</span>
                            </Title>
                        )}
                    </>
                )}

                {documentCategory && (
                    <>
                        {showSelectionSections && <Divider className="mb-32 mt-32" />}

                        {showProcessSection && (
                            <>
                                <Title type={3} className="mb-20">
                                    {t(
                                        'advisoryDashboard.documents.uploadDocuments.chooseProcess',
                                    )}
                                </Title>

                                <div className="mb-32">
                                    <Controller
                                        name="processes"
                                        control={control}
                                        render={({ field }) => (
                                            <>
                                                <CheckboxGroup
                                                    options={processCheckboxOptions}
                                                    onChange={field.onChange}
                                                    value={field.value}
                                                    size="big"
                                                    horizontal
                                                    className="upload-bundled-documents__checkbox-group mb-8"
                                                />
                                                {(errors.processes as any)?.message && (
                                                    <FormControlMessage className="mb-32">
                                                        {
                                                            (errors.processes as any)
                                                                .message
                                                        }
                                                    </FormControlMessage>
                                                )}
                                            </>
                                        )}
                                    />
                                </div>
                            </>
                        )}

                        {showProductSection && (
                            <>
                                <Title type={3} className="mb-20">
                                    {t(
                                        'advisoryDashboard.documents.uploadDocuments.chooseProducts',
                                    )}
                                </Title>

                                <Controller
                                    name="products"
                                    control={control}
                                    render={({ field }) => (
                                        <>
                                            <CheckboxGroup
                                                options={productCheckboxOptions}
                                                onChange={field.onChange}
                                                value={field.value}
                                                size="big"
                                                horizontal
                                                className="upload-bundled-documents__checkbox-group mb-8"
                                            />
                                            {(errors.products as any)?.message && (
                                                <FormControlMessage>
                                                    {(errors.products as any).message}
                                                </FormControlMessage>
                                            )}
                                        </>
                                    )}
                                />
                            </>
                        )}

                        <Divider className="mb-32 mt-32" />

                        <Title type={3} className="mb-20">
                            {t(
                                'advisoryDashboard.documents.uploadDocuments.uploadNewDocuments',
                            )}
                        </Title>

                        <div className="upload-bundled-documents__documents-to-upload">
                            {languages?.map(({ value, label }) => (
                                <div
                                    key={value}
                                    className="mb-16 flex gap-8 align-center"
                                >
                                    <Title
                                        type={3}
                                        className="font-normal mb-0"
                                        style={{ minWidth: 120 }}
                                    >
                                        {label}
                                    </Title>

                                    <Controller
                                        name={`documents.${value}` as any}
                                        control={control}
                                        render={({ field }) => (
                                            <DocBoxGeneric
                                                docName={
                                                    field.value?.name
                                                    || `${selectedDocumentCategory?.label} ${value.toLocaleUpperCase()}.pdf`
                                                }
                                                uploadStatus={
                                                    field.value
                                                        ? DocStatus.ReadyToSubmit
                                                        : DocStatus.ReadyToUpload
                                                }
                                                canDownload={false}
                                                onDownload={() => {}}
                                                canUpload
                                                handleUpload={({ files }) => field.onChange(
                                                    files?.[0] ?? null,
                                                )}
                                            />
                                        )}
                                    />
                                </div>
                            ))}

                            <div className="mb-32 flex gap-8 align-center">
                                <div style={{ minWidth: 120 }} />
                                <p className="mb-8 upload-bundled-documents__documents-to-upload-text">
                                    {t(
                                        'advisoryDashboard.documents.uploadDocuments.pdfInfo',
                                        { maxSize: 30 },
                                    )}
                                </p>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <div className="content-bottom">
                <Divider className="0" />

                <ButtonsBlockRow
                    additionalButton={{
                        text: t('clientDashboard.cancel'),
                        onClick: onBack,
                        disabled: backDisabled || isUploading,
                    }}
                    primaryButton={{
                        text: t(
                            'advisoryDashboard.documents.uploadDocuments.button.replace',
                        ),
                        onClick: handleSubmit(onReplaceDocuments),
                        disabled: saveButtonDisabled || !hasAtLeastOneDocument,
                        loading: isUploading,
                    }}
                />
            </div>

            {documentsToReplace && (
                <ReplaceDocumentsConfirmModal
                    documentName={replacedDocumentName}
                    languages={uploadedLanguages}
                    processes={selectedProcessLabels}
                    products={selectedProductLabels}
                    isLoading={isUploading}
                    onConfirm={confirmReplaceDocuments}
                    onCancel={closeConfirmation}
                />
            )}
        </form>
    );
};

export default UploadBundledDocuments;
